/**
 * Phone bubbles (below 600 px; snippets/sunday-bubbles.liquid, AR 24 Oct).
 *
 * Call bubble (right): always there, phone only. In the store's hours (India time) it calls (green dot);
 * outside them, or with no hours, it opens WhatsApp. It steps aside only when something else already
 * offers the same thing on screen (a tel: or WhatsApp link: the talk card, the footer, a landing header's
 * number, a page bar's WhatsApp), while a sheet, the menu or a dialog is open, while the keyboard is up,
 * and while it would sit on a button, a field or a price. 30 Oct (AR go 2): it keeps one home, bottom right, on
 * every page; the page bars leave that corner free (assets/sunday-bubbles.css). The slide-in steps away while a bar is in.
 *
 * Slide-in (left): only at the moment of doubt.
 *   Before Help me choose: "Help me choose", once per page, when the three mattresses have scrolled off the
 *   top (data-sd-slide="three": home, mattress collections) or at the end of the article ("end"); it leaves
 *   when they scroll back up and doesn't come back on that page. At most twice a visit.
 *   After Help me choose: "Your pick" after the first scroll, on any visit; a tap opens that mattress page.
 *   Never while a Help me choose button is on screen, at the talk card or footer, or while a sheet is open.
 */
import { current, pickHref } from '@sunday/choose';

const phone = matchMedia('(max-width: 599px)');
const html = document.documentElement;
const bub = document.querySelector('[data-sd-bub]');
const slide = document.querySelector('[data-sd-slide]');
const BARS = '.sd-mbar, .sd-a2bar, .sd-apbar, .sd-cartbar, .sd-mx-sbar, .sd-dock';
const BUSY = '[aria-modal="true"], dialog[open]';
const KEY = 'sunday-slide-n';
/* 30 Oct (AR go 3): on the three mattress pages the bubble waits until the size block (Your size … Add to cart) has
   gone above it, and never sits on a button there (it covered "More sizes" and in / cm on the first screen) */
const szTop = document.querySelector('sunday-m-hero .sd-mh-size');
const szAdd = szTop && document.querySelector('sunday-m-hero .sd-mh-add');

const seen = (el) => {
  if (!el || !el.getClientRects().length) return null;
  const r = el.getBoundingClientRect();
  if (r.bottom <= 0 || r.top >= innerHeight || r.width === 0) return null;
  const cs = getComputedStyle(el);
  return cs.visibility === 'hidden' || +cs.opacity === 0 ? null : r;
};

/* 25 Oct (speed): what is on screen is kept by IntersectionObservers, so a scroll frame no longer walks the page.
   Each group holds only the elements on screen now; the per-frame check looks at those few (still confirming
   they are really shown). The lists are found again every 2 s, so links that arrive later (sheets, the cart) count. */
const GROUPS = { tel: 'a[href^="tel:"]', wa: 'a[href*="wa.me/"]', talk: '.sd-talk, .sd-ft', pill: '[data-sd-pill-open], [data-hc-pill], a[href="#build"]' };
const live = {};
const watched = new WeakSet();
const io = 'IntersectionObserver' in window
  ? new IntersectionObserver((es) => {
      es.forEach((e) => {
        const g = e.target.__sdg;
        if (e.isIntersecting) live[g].add(e.target);
        else live[g].delete(e.target);
      });
      ask();
    })
  : null;
let dialogs = [];
let bars = [];
const ours = (e) => (bub && bub.contains(e)) || (slide && slide.contains(e));
const scan = () => {
  dialogs = [...document.querySelectorAll(BUSY)];
  bars = [...document.querySelectorAll(BARS)].filter((b) => b !== bub);
  if (!io) return;
  Object.entries(GROUPS).forEach(([g, sel]) => {
    live[g] = live[g] || new Set();
    document.querySelectorAll(sel).forEach((e) => {
      if (watched.has(e) || ours(e)) return;
      watched.add(e);
      e.__sdg = g;
      io.observe(e);
    });
  });
};
const onScreen = (...gs) =>
  gs.some((g) => (io ? [...(live[g] || [])].some((e) => e.isConnected && seen(e)) : [...document.querySelectorAll(GROUPS[g])].some((e) => !ours(e) && seen(e))));
const busy = () =>
  html.classList.contains('sd-hc-open') ||
  !!document.querySelector('.sd-ov:not([hidden])') ||
  dialogs.some((d) => d.isConnected && d.matches(BUSY) && d.getClientRects().length) ||
  !!document.activeElement?.matches?.('input:not([type=checkbox]):not([type=radio]), textarea, select');

/* The height of a page's own bottom bar while it is on screen (the bars are fixed by their own styles; a bar that is
   away sits below the screen). 30 Oct (AR go 2): nothing rides above a bar any more; the slide-in steps away while one is in. */
const lift = () => {
  let top = innerHeight;
  bars.forEach((b) => {
    if (!b.isConnected || !b.getClientRects().length) return;
    const r = b.getBoundingClientRect();
    if (r.top < innerHeight - 2 && r.bottom > innerHeight - 40) top = Math.min(top, r.top);
  });
  return Math.max(0, innerHeight - top);
};

/* Would this box sit on a button, a field or a price? Three points (both top corners and the middle) cover a 52 px
   bubble and a 48 px strip; the bottom corners never found anything the top ones missed. */
const covers = (box) => {
  if (!box) return false;
  const r = box.getBoundingClientRect();
  const pts = [[r.left + 4, r.top + 4], [r.right - 4, r.top + 4], [(r.left + r.right) / 2, (r.top + r.bottom) / 2]];
  return pts.some(([x, y]) =>
    document.elementsFromPoint(x, y).some((e) => {
      if (box.contains(e) || e.closest(BARS)) return false;
      const t = e.closest('button, summary, input, select, textarea, .sd-btn, [data-dock-price], [data-price], .sd-price'); /* summary: a question row's + (25 Oct) */
      if (!t || ours(t)) return false;
      const tr = t.getBoundingClientRect();
      return tr.width < innerWidth * 0.9 || tr.height < 80; /* a whole card or band is not a button */
    })
  );
};

/* ---------- call bubble ---------- */
/* 26 Oct: the language pages translate the hours ("ఉదయం 10 నుంచి సాయంత్రం 7 వరకు"), which the English-only
   reading missed, so the bubble showed WhatsApp all day there. Read the first two times in the text, as the talk
   card does (assets/sunday-talk.js): am/pm when given; without it, a closing hour at or before the opening hour is
   in the afternoon (10 … 7 = 7 pm). Read once, as minutes in India time. */
const span = (() => {
  if (!bub || !bub.dataset.tel || !bub.dataset.hours) return null;
  const re = /(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/gi;
  const t = [];
  let m;
  while ((m = re.exec(bub.dataset.hours)) && t.length < 2) t.push({ min: (Number(m[1]) % 12 + (/pm/i.test(m[3] || '') ? 12 : 0)) * 60 + Number(m[2] || 0), ap: !!m[3] });
  if (t.length < 2) return null;
  if (!t[1].ap && t[1].min <= t[0].min) t[1].min += 720;
  return [t[0].min, t[1].min];
})();
const bubw = bub ? document.querySelector('[data-sd-bubw]') : null;
let bubwXY = '';
const openNow = () => {
  if (bub && bub.hasAttribute('data-always')) return true; /* 4 Nov (AR): calls answered 24/7 */
  if (!span) return false;
  const d = new Date();
  const ist = (d.getUTCHours() * 60 + d.getUTCMinutes() + 330) % 1440;
  return ist >= span[0] && ist < span[1];
};
const setMode = () => {
  if (!bub) return;
  const call = openNow() || !bub.dataset.wa;
  bub.dataset.mode = call ? 'call' : 'wa';
  bub.href = call ? bub.dataset.tel : bub.dataset.wa;
  bub.setAttribute('aria-label', call ? bub.dataset.callLabel || '' : bub.dataset.waLabel || '');
  if (call) bub.removeAttribute('target');
  else {
    bub.target = '_blank';
    bub.rel = 'noopener';
  }
};

/* ---------- slide-in ---------- */
const mode = slide && !slide.hasAttribute('data-cart-mat') ? slide.dataset.sdSlide : ''; /* a mattress in the cart: no slide-in */
const help = slide && slide.querySelector('[data-sd-slide-help]');
const pickA = slide && slide.querySelector('[data-sd-slide-pick]');
let pk = null;
let used = false; /* "Help me choose" already shown and left on this page */
let shown = false;
const count = () => {
  try {
    return Number(sessionStorage.getItem(KEY) || 0);
  } catch {
    return 0;
  }
};
const bump = () => {
  try {
    sessionStorage.setItem(KEY, String(count() + 1));
  } catch {}
};
const paintPick = () => {
  if (!slide) return;
  pk = current();
  if (help) help.hidden = !!pk;
  if (pickA) {
    pickA.hidden = !pk;
    if (pk) {
      pickA.href = pickHref() || pk.url;
      pickA.querySelector('[data-sd-slide-name]').textContent = pk.name;
      pickA.querySelector('[data-sd-slide-dot]').style.setProperty('--sd-c', pk.colour || '');
    }
  }
};
const trigger = () => {
  if (mode === 'three') return document.querySelector('.sd-mtrow, [data-mx-cards]');
  if (mode === 'end') return document.querySelector('main article, #MainContent article');
  return null;
};
const wantSlide = () => {
  if (!slide || !mode) return false;
  if (onScreen('pill', 'talk')) return false;
  if (pk) return scrollY > innerHeight * 0.6 && mode !== '';
  if (mode === 'pick' || used) return false;
  const t = trigger();
  if (!t) return false;
  const r = t.getBoundingClientRect();
  const due = mode === 'three' ? r.bottom < 0 : r.bottom < innerHeight;
  if (!due && shown) {
    used = true; /* scrolled back up: it leaves and stays gone on this page */
    shown = false;
    return false;
  }
  if (due && !shown && count() >= 2) return false;
  if (due && !shown) {
    shown = true;
    bump();
  }
  return due;
};

/* ---------- language label (26 Oct, AR) ----------
   Slides out from behind the bubble (Call in office hours, WhatsApp outside them): 3 s after a page loads, for 6 s,
   once per page on the first three pages of a visit. A visit ends after 30 minutes with no new page
   (localStorage, so it survives a tab kept open for days). Never while the slide-in shows or the bubble steps aside. */
const lab = bub ? document.querySelector('[data-sd-bubl]') : null;
const LK = 'sunday-bubl';
const VISIT_GAP = 30 * 60000;
const LAB_PAGES = 3;
let labOn = false;
let labDone = !lab;
let labReady = false;
let labXY = '';
/* count this page in the visit; no storage → don't show (it would show on every page) */
const labPage = (() => {
  if (!lab) return 99;
  try {
    const now = Date.now();
    const [n, t] = (localStorage.getItem(LK) || '0|0').split('|').map(Number);
    const page = (now - (t || 0) > VISIT_GAP ? 0 : n || 0) + 1;
    localStorage.setItem(LK, page + '|' + now);
    return page;
  } catch {
    return 99;
  }
})();
if (labPage > LAB_PAGES) labDone = true;
const labStart = () => {
  labOn = true;
  setTimeout(() => {
    labOn = false;
    labDone = true;
    ask();
  }, 6000);
};
const wantLab = (bubOff, slideOn) => {
  if (labDone) return false;
  const ok = !bubOff && !slideOn;
  if (labOn) {
    if (!ok) labDone = true; /* it leaves early and stays gone on this page */
    return ok;
  }
  return labReady && ok;
};

/* ---------- one check per frame ----------
   25 Oct (speed): every measurement first, then the changes, and a change only when something changed, so a
   scroll frame lays the page out once. */
let tick = 0;
const check = () => {
  tick = 0;
  if (!phone.matches) return;
  if (!html.classList.contains('sd-bubs')) html.classList.add('sd-bubs');
  if (bub && bub.hidden) bub.hidden = false;
  if (slide && slide.hidden) slide.hidden = false;
  /* reads */
  const b = busy();
  const barIn = slide || (lab && !labDone) ? lift() > 0 : false; /* 30 Oct (AR go 2): the slide-in and the language label step away while a bar is in */
  let off = null;
  if (bub) {
    /* AR 26 Oct: stays put while scrolling; hides only at the talk card/footer, a sheet, or the keyboard */
    off = b || onScreen('talk');
    if (!off && szAdd) {
      const r = bub.getBoundingClientRect();
      const g = (52 - r.width) / 2 + 4; /* its full size (it shrinks while away), plus a little room */
      const box = { getBoundingClientRect: () => new DOMRect(r.left - g, r.top - g, r.width + 2 * g, r.height + 2 * g), contains: (e) => bub.contains(e) };
      off = szAdd.getBoundingClientRect().bottom > r.top - g || covers(box);
    }
  }
  const on = slide ? !b && !barIn && wantSlide() && !covers(slide) : null;
  let lw = false;
  let xy = labXY;
  if (lab && !labDone) {
    const r = bub.getBoundingClientRect();
    xy = Math.round(innerWidth - r.left - r.width / 2) + '|' + Math.round(r.top + r.height / 2);
    lw = wantLab(off || barIn, on) && (labOn || !covers(lab));
  }
  /* writes */
  if (bub && bub.classList.contains('sd-off') !== off) {
    bub.classList.toggle('sd-off', off);
    bub.tabIndex = off ? -1 : 0;
  }
  /* 4 Nov (AR): the white WhatsApp bubble sits just above the Call bubble and comes and goes with it */
  if (bubw && bub) {
    if (bubw.hidden) bubw.hidden = false;
    const r = bub.getBoundingClientRect();
    const pos = Math.round(innerHeight - r.top + 8) + '|' + Math.round(innerWidth - r.right + (r.width - 40) / 2);
    if (pos !== bubwXY && r.width) {
      const [bt, rt] = pos.split('|');
      bubw.style.bottom = bt + 'px';
      bubw.style.right = rt + 'px';
      bubwXY = pos;
    }
    if (bubw.classList.contains('sd-off') !== !!off) {
      bubw.classList.toggle('sd-off', !!off);
      bubw.tabIndex = off ? -1 : 0;
    }
  }
  if (lab) {
    if (xy !== labXY) {
      const [x, y] = xy.split('|');
      lab.style.setProperty('--bx', x + 'px');
      lab.style.setProperty('--by', y + 'px');
      labXY = xy;
    }
    if (lab.getAttribute('href') !== bub.getAttribute('href')) {
      /* the label does what the bubble does: call in office hours, WhatsApp outside them */
      lab.href = bub.href;
      if (bub.target) {
        lab.target = bub.target;
        lab.rel = 'noopener';
      } else lab.removeAttribute('target');
    }
    if (lw && !labOn) labStart();
    if (lab.hidden) lab.hidden = false;
    if (lab.classList.contains('sd-off') === lw) lab.classList.toggle('sd-off', !lw);
  }
  if (slide && slide.classList.contains('sd-off') === on) {
    slide.classList.toggle('sd-off', !on);
    slide.querySelectorAll('a, button').forEach((e) => (e.tabIndex = on ? 0 : -1));
  }
};
const ask = () => {
  if (!tick) tick = requestAnimationFrame(check);
};

if (bub || slide) {
  setMode();
  paintPick();
  scan();
  setInterval(scan, 2000);
  if (slide) slide.classList.add('sd-off');
  if (bub) bub.classList.add('sd-off');
  addEventListener('scroll', ask, { passive: true });
  document.addEventListener('scroll', ask, { passive: true, capture: true });
  addEventListener('resize', ask);
  document.addEventListener('focusin', ask);
  document.addEventListener('focusout', () => setTimeout(ask, 0));
  document.addEventListener('click', () => setTimeout(ask, 50));
  document.addEventListener('sunday:bar', ask);
  document.addEventListener('sunday:pick', () => {
    paintPick();
    ask();
  });
  if ('MutationObserver' in window) new MutationObserver(ask).observe(html, { attributes: true, attributeFilter: ['class'] });
  phone.addEventListener('change', ask);
  setInterval(() => {
    setMode();
    ask();
  }, 60000);
  if (lab) {
    setTimeout(() => {
      labReady = true;
      ask();
    }, 3000);
  }
  /* 31 Oct (speed): the first check waits for the first frame (still before it paints), so loading the page
     doesn't lay the whole page out here while other scripts are still changing it */
  ask();
}
